import { Box } from '@mui/material';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { getStores } from '../../api/storeApi';
import { deleteTrailer, getTrailerList, updateMultipleTrailers, updateTrailerLocation } from '../../api/trailerApi';
import ModalForm from '../../components/AddModal';
import AppDialogMessage from '../../components/Common/AppDialogMessage';
import TrailerBottomPagination from '../../components/Trailers/TrailerBottomPagination';
import TrailerFilterModal from '../../components/Trailers/TrailerFilterModal';
import TrailerMapModal from '../../components/Trailers/TrailerMapModal';
import TrailerTable from '../../components/Trailers/TrailerTable';
import TrailerTopFilters from '../../components/Trailers/TrailerTopFilters';
import { useAppDialog } from '../../Hooks/useAppDialog';
import { useDebounce } from '../../Hooks/useDebounce';


type TrailerType = any;

type FilterState = {
    trailer_no: string;
    product: string;
    rental_company: string;
    lease_agreement_no: string;
    payer: string;
    note: string;
    dclocation: string;
};

type DateState = {
    start_dt?: string;
    end_dt?: string;
};

type PaginationState = {
    page: number;
    limit: number;
    total: number;
    totalRows: number;
};

const TrailerProducts: React.FC = () => {
    const [trailers, setTrailers] = useState<TrailerType[]>([]);
    const [expandedRows, setExpandedRows] = useState<string[]>([]);
    const [updatedTrailers, setUpdatedTrailers] = useState<string[]>([]);

    const [pagination, setPagination] = useState<PaginationState>({
        page: 1,
        limit: 15,
        total: 1,
        totalRows: 0,
    });

    const [filterModal, setFilterModal] = useState(false);
    const [addModal, setAddModal] = useState(false);
    const [mapModal, setMapModal] = useState(false);
    const [trailer, setTrailer] = useState<TrailerType | null>(null);
    const { dialog, openDialog, closeDialog } = useAppDialog();
    const [date, setDate] = useState<DateState>({});

    const companies = useMemo(
        () => ['SMARTWAY', 'G2000', 'SVP SPORTS', 'OTHERS'],
        []
    );

    const initialFilter = useMemo<FilterState>(
        () => ({
            trailer_no: '',
            product: '',
            rental_company: '',
            lease_agreement_no: '',
            payer: '',
            note: '',
            dclocation: '',
        }),
        []
    );

    const [filter, setFilter] = useState<FilterState>(initialFilter);

    const debouncedTrailerSearch = useDebounce(filter.trailer_no, 500);
    const debouncedProductSearch = useDebounce(filter.product, 500);

    const [storeOptions, setStoreOptions] = useState<{ label: string; value: string }[]>([]);

    const [columns, setColumns] = useState([
        { accessorKey: 'trailer_no', header: 'Trailer No', width: 150 },
        { accessorKey: 'note', header: 'Note', width: 250 },
        { accessorKey: 'images', header: 'Files', width: 150 },
        { accessorKey: 'rental_company', header: 'Rental', width: 150 },
        { accessorKey: 'products', header: 'Skid', width: 150 },
        { accessorKey: 'payer', header: 'Invoice To', width: 150 },
        { accessorKey: 'zone', header: 'Zone', width: 150 },
        { accessorKey: 'dclocation', header: 'Location', width: 90 },
        { accessorKey: 'received_date', header: 'Received Date', width: 100 },
        { accessorKey: 'lease_agreement_no', header: 'Lease No', width: 150 },
        { accessorKey: 'updated_at', header: 'Updated At', width: 150 },
        { accessorKey: 'qr', header: 'QR', width: 50 },
        { accessorKey: 'info', header: 'Map', width: 80 },
        { accessorKey: 'delete', header: 'Delete', width: 80 },
    ]);
    // Resize columns based on the width
    const handleColumnResize = (accessorKey: string, width: number) => {
        setColumns((prev) =>
            prev.map((col) =>
                col.accessorKey === accessorKey
                    ? { ...col, width: Math.max(60, width) }
                    : col
            )
        );
    };
    useEffect(() => {
        getStores()
            .then((data) => {
                const options = [
                    { label: 'Other', value: 'Other' },
                    ...data
                        .sort((a: any, b: any) => {
                            const aStartsWithD = a.location.toLowerCase().startsWith('d');
                            const bStartsWithD = b.location.toLowerCase().startsWith('d');

                            if (aStartsWithD && !bStartsWithD) return -1;
                            if (!aStartsWithD && bStartsWithD) return 1;

                            return a.location.localeCompare(b.location);
                        })
                        .map((store: any) => ({
                            label: store.location,
                            value: store.location,
                        })),
                ];

                setStoreOptions(options);
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        getTrailers(pagination.page, pagination.limit, {
            ...filter,
            trailer_no: debouncedTrailerSearch,
            product: debouncedProductSearch,
        }, date);
    }, [pagination.page, pagination.limit, debouncedTrailerSearch, debouncedProductSearch]);

    const getTrailers = async (
        page: number,
        limit: number,
        filterPayload?: Partial<FilterState>,
        datePayload?: DateState
    ) => {
        try {
            const result = await getTrailerList({
                page,
                limit,
                filters: filterPayload,
                dates: datePayload,
            });

            setTrailers(result.data || []);

            setPagination((prev) => ({
                ...prev,
                page,
                total: Math.ceil((result.totalRows || 0) / prev.limit) || 1,
                totalRows: result.totalRows || 0,
            }));
        } catch (error) {
            console.log('Error in getTrailerList:', error);
        }
    };

    const addUpdatedTrailerId = (id: string) => {
        setUpdatedTrailers((prev) => {
            if (!prev.includes(id)) {
                return [...prev, id];
            }
            return prev;
        });
    };

    const handleOnChangeText = (e: ChangeEvent<HTMLInputElement>, id: string) => {
        setTrailers((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, [e.target.name]: e.target.value } : item
            )
        );
        addUpdatedTrailerId(id);
    };

    const handleRowClick = (selectedTrailer: TrailerType) => {
        if (!selectedTrailer?.id) return;

        setExpandedRows((prev) =>
            prev.includes(selectedTrailer.id)
                ? prev.filter((id) => id !== selectedTrailer.id)
                : [...prev, selectedTrailer.id]
        );
    };

    const handleAddRow = (id: string) => {
        setTrailers((prev) =>
            prev.map((item) =>
                item.id === id
                    ? {
                        ...item,
                        products: [...(item.products || []), { label: '', description: '', skid: 0 }],
                    }
                    : item
            )
        );
        addUpdatedTrailerId(id);
    };

    const handleDeleteRow = (id: string, index: number) => {
        setTrailers((prev) =>
            prev.map((item) =>
                item.id === id
                    ? {
                        ...item,
                        products: item.products.filter((_: any, i: number) => i !== index),
                    }
                    : item
            )
        );
        addUpdatedTrailerId(id);
    };

    const handleDeleteTrailer = async (trailer_no: string) => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete ${trailer_no}?`
        );

        if (!confirmDelete) return;

        try {
            await deleteTrailer(trailer_no);

            setTrailers((prev) =>
                prev.filter((item: any) => item.trailer_no !== trailer_no)
            );

            setPagination((prev) => ({
                ...prev,
                totalRows: Math.max(prev.totalRows - 1, 0),
            }));

            openDialog({
                variant: 'success',
                title: 'Trailer Deleted',
                message: `Trailer ${trailer_no} deleted successfully.`,
                confirmText: 'Okay',
                hideCancel: true,
            });
        } catch (error: any) {
            console.log('Error deleting trailer:', error);

            openDialog({
                variant: 'error',
                title: 'Delete Failed',
                message:
                    error?.message ||
                    'Something went wrong while deleting the trailer.',
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    };

    const handleDataChange = (column: string, value: string, id: string) => {
        setTrailers((prev) =>
            prev.map((item) => (item.id === id ? { ...item, [column]: value } : item))
        );
        addUpdatedTrailerId(id);
    };

    const handleInfoChange = (column: string, value: string, id: string) => {
        setTrailers((prev) =>
            prev.map((item) =>
                item.id === id
                    ? { ...item, info: { ...item.info, [column]: value } }
                    : item
            )
        );
        addUpdatedTrailerId(id);
    };

    const handleProductChange = (
        value: string,
        id: string,
        index: number,
        column: string
    ) => {
        setTrailers((prev) =>
            prev.map((item) =>
                item.id === id
                    ? {
                        ...item,
                        products: item.products.map((product: any, i: number) =>
                            i === index
                                ? {
                                    ...product,
                                    [column]: column === 'skid' ? parseInt(value || '0', 10) : value,
                                }
                                : product
                        ),
                    }
                    : item
            )
        );
        addUpdatedTrailerId(id);
    };

    const handleDateChange = (e: ChangeEvent<HTMLInputElement>, id: string) => {
        setTrailers((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, received_date: e.target.value } : item
            )
        );
        addUpdatedTrailerId(id);
    };

    const handleSave = async () => {
        if (updatedTrailers.length === 0) {
            openDialog({
                variant: 'info',
                title: 'No Changes',
                message: 'Everything is up to date.',
                confirmText: 'Okay',
                hideCancel: true,
            });
            return;
        }

        try {
            const trailersToSave = trailers.filter((item) =>
                updatedTrailers.includes(item.id)
            );

            const result = await updateMultipleTrailers(trailersToSave);

            setUpdatedTrailers([]);

            openDialog({
                variant: 'success',
                title: 'Trailers Updated',
                message: result.message || 'Trailers updated successfully.',
                confirmText: 'Okay',
                hideCancel: true,
            });
        } catch (error: any) {
            console.log('Error saving the data', error);

            openDialog({
                variant: 'error',
                title: 'Update Failed',
                message: error?.message || 'Error saving the data.',
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    };

    const handleFilterChange = (column: keyof FilterState, value: string) => {
        setFilter((prev) => ({ ...prev, [column]: value }));
    };

    const handleApplyFilter = () => {
        setPagination((prev) => ({ ...prev, page: 1 }));
        getTrailers(
            1,
            pagination.limit,
            {
                ...filter,
                trailer_no: filter.trailer_no,
                product: filter.product,
            },
            date
        );
        setFilterModal(false);
    };

    const handleResetFilter = () => {
        setFilter(initialFilter);
        setDate({});
        setPagination((prev) => ({ ...prev, page: 1 }));
        getTrailers(1, pagination.limit, initialFilter, {});
        setFilterModal(false);
    };

    const getCurrentLocation = () =>
        new Promise<{ lat: number; lon: number }>((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation is not supported by this browser.'));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        lat: position.coords.latitude,
                        lon: position.coords.longitude,
                    });
                },
                (error) => reject(error)
            );
        });

    const openInGoogleMaps = (lat: number, lon: number) => {
        const url = `https://www.google.com/maps?q=${lat},${lon}`;
        window.open(url, '_blank');
    };

    const handleCurrentLocation = async () => {
        try {
            if (!trailer) return;

            const loc = await getCurrentLocation();

            setTrailer((prev: any) => ({
                ...prev,
                info: { ...prev.info, lat: loc.lat, lon: loc.lon },
            }));

            setTrailers((prev) =>
                prev.map((item: any) =>
                    item.id === trailer.id
                        ? { ...item, info: { ...item.info, lat: loc.lat, lon: loc.lon } }
                        : item
                )
            );

            addUpdatedTrailerId(trailer.id);
        } catch (error) {
            console.log(error);
            alert('Unable to get current location.');
        }
    };

    const handleSetMap = async () => {
        if (!trailer) return;

        try {
            const trailerToSave = {
                ...trailer,
                info: {
                    ...trailer.info,
                    lon: trailer?.info?.lon || '',
                    lat: trailer?.info?.lat || '',
                },
            };

            const result = await updateTrailerLocation(trailerToSave);

            setTrailers((prev) =>
                prev.map((item: any) =>
                    item.id === trailerToSave.id ? trailerToSave : item
                )
            );

            openDialog({
                variant: 'success',
                title: 'Location Updated',
                message: result.message || 'Location updated successfully.',
                confirmText: 'Okay',
                hideCancel: true,
            });

            setMapModal(false);
        } catch (error: any) {
            console.log('Error saving trailer map changes:', error);

            openDialog({
                variant: 'error',
                title: 'Update Failed',
                message: error?.message || 'Error in updating trailer info',
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    };

    const handleGetMap = async (info: any) => {
        if (!info?.lat || !info?.lon) {
            alert('Location not available.');
            return;
        }

        openInGoogleMaps(info.lat, info.lon);
    };

    const handleMapOpen = (selectedTrailer: TrailerType) => {
        setTrailer(selectedTrailer);
        setMapModal(true);
    };

    return (
        <Box >
            <ModalForm
                opened={addModal}
                onClose={() => setAddModal(false)}
                setTrailers={setTrailers}
            />

            <TrailerFilterModal
                open={filterModal}
                filter={filter}
                date={date}
                storeOptions={storeOptions}
                onClose={() => setFilterModal(false)}
                onFilterChange={handleFilterChange}
                onDateChange={(key, value) =>
                    setDate((prev) => ({ ...prev, [key]: value }))
                }
                onApply={handleApplyFilter}
                onReset={handleResetFilter}
            />

            <TrailerMapModal
                open={mapModal}
                trailer={trailer}
                onClose={() => setMapModal(false)}
                onTrailerChange={setTrailer}
                onGetCurrentLocation={handleCurrentLocation}
                onSaveLocation={handleSetMap}
            />

            <TrailerTopFilters
                trailerSearch={filter.trailer_no}
                productSearch={filter.product}
                updatedCount={updatedTrailers.length}
                onTrailerSearchChange={(value) =>
                    setFilter((prev) => ({ ...prev, trailer_no: value }))
                }
                onProductSearchChange={(value) =>
                    setFilter((prev) => ({ ...prev, product: value }))
                }
                onOpenAdd={() => setAddModal(true)}
                onOpenFilter={() => setFilterModal(true)}
                onSave={handleSave}
            />
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'auto', height: { xs: 'calc(100vh - 43vh)', sm: 'calc(100vh - 38vh)', md: 'calc(100vh - 33vh)', lg: 'calc(100vh - 32vh)' }, }}>
                <TrailerTable
                    trailers={trailers}
                    columns={columns}
                    expandedRows={expandedRows}
                    companies={companies}
                    storeOptions={storeOptions}
                    onColumnResize={handleColumnResize}
                    setTrailers={setTrailers}
                    onRowClick={handleRowClick}
                    onTextChange={handleOnChangeText}
                    onDateChange={handleDateChange}
                    onDataChange={handleDataChange}
                    onInfoChange={handleInfoChange}
                    onMapOpen={handleMapOpen}
                    onGetMap={handleGetMap}
                    onAddRow={handleAddRow}
                    onDeleteRow={handleDeleteRow}
                    onDeleteTrailer={handleDeleteTrailer}
                    onProductChange={handleProductChange}
                />
            </Box>

            <TrailerBottomPagination
                page={pagination.page}
                total={pagination.total}
                totalRows={pagination.totalRows}
                onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
            />
            <AppDialogMessage
                open={dialog.open}
                title={dialog.title}
                message={dialog.message}
                variant={dialog.variant}
                confirmText={dialog.confirmText}
                cancelText={dialog.cancelText}
                hideCancel={dialog.hideCancel}
                onConfirm={dialog.onConfirm}
                onClose={closeDialog}
            />
        </Box>
    );
};

export default TrailerProducts;